import { Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { ActionsMenu } from "@zfp-akademie-a-s/zfp-design";
import { SettingsIcon } from "Components/_Shared/Icons/Icons";
import { getGetAssetAllocationOverviewQueryKey, useSetPortfolioAssetAllocationRequired } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { PortfolioAssetAllocationOverview } from "Infrastructure/ApiZfp/Schema";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";
import { GoalFulfillmentFilterType, useGoalsFulfillmentContext } from "./GoalsFulfillmentContext";

type Props = {
  overview: PortfolioAssetAllocationOverview;
};

type FilterOption = {
  id: GoalFulfillmentFilterType;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
  onChange?: (value: boolean) => void;
  checked?: boolean;
};

export const GoalFulfillmentSettings = () => {
  const { assetAllocationOverview: overview, isAssetAllocationOverviewSuccess: isSuccess, setFilterType } = useGoalsFulfillmentContext();

  const isInitialized = useRef(false);

  useEffect(() => {
    if (isSuccess && overview && !isInitialized.current) {
      if (overview.isPortfolioAssetAllocationRequired) {
        setFilterType(GoalFulfillmentFilterType.PortfolioDetail);
      }

      isInitialized.current = true;
    }
  }, [isSuccess, overview, setFilterType]);

  if (!overview) return null;

  return <GoalFullfillmentSettingsContent overview={overview} />;
};

const GoalFullfillmentSettingsContent = ({ overview }: Props) => {
  const { t } = useResource();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { analysisId, versionId } = useContext(AnalysisDetailContext);
  const { toggleFilter, selectedFilters } = useGoalsFulfillmentContext();
  const queryClient = useQueryClient();

  const { mutate: setAllocationRequired } = useSetPortfolioAssetAllocationRequired();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filterOptions: FilterOption[] = [
    {
      id: GoalFulfillmentFilterType.PortfolioDetail,
      label: t(Resources.AnalysisDetail.GoalsFulfillment.SettingsMenu.PortfolioDistribution),
      disabled: !overview.isPortfolioAssetAllocationEditable,
      disabledTooltip: t(Resources.AnalysisDetail.GoalsFulfillment.SettingsMenu.CharacteristicsToggleDisabled),
      onChange: (value: boolean) =>
        setAllocationRequired(
          {
            analysisId,
            versionId,
            data: { isPortfolioAssetAllocationRequired: value },
          },
          {
            onSuccess: () => {
              queryClient.invalidateQueries({ queryKey: getGetAssetAllocationOverviewQueryKey(analysisId, versionId) });
            },
            onError: () => {
              toast.error(t(Resources.AnalysisDetail.GoalsFulfillment.SettingsMenu.SettingsEditFailed));
              toggleFilter(GoalFulfillmentFilterType.PortfolioDetail);
            },
          },
        ),
      checked: overview?.isPortfolioAssetAllocationRequired ?? false,
    },
    { id: GoalFulfillmentFilterType.FutureAmount, label: t(Resources.AnalysisDetail.GoalsFulfillment.SettingsMenu.FutureValueTitle) },
  ];

  return (
    <div>
      <Button
        variant="text"
        color="primary"
        onClick={handleClick}
        aria-controls={open ? "goals-fulfillment-settings-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        startIcon={<SettingsIcon />}
        sx={{ textTransform: "none" }}
      >
        {t(Resources.AnalysisDetail.GoalsFulfillment.SettingsMenu.Title)}
      </Button>

      <ActionsMenu
        id="goals-fulfillment-settings-menu"
        anchorEl={anchorEl}
        onClose={handleClose}
        items={filterOptions.map((option) => {
          const isChecked = selectedFilters.includes(option.id);
          return {
            label: option.label,
            checked: isChecked,
            disabled: option.disabled,
            disabledReason: option.disabledTooltip,
            onClick: () => {
              toggleFilter(option.id);
              option.onChange?.(!isChecked);
            },
          };
        })}
      />
    </div>
  );
};
